import { useEffect, useState } from "react";
import type { GraphyClient } from "../api/client";
import { ApiError } from "../api/client";
import type { CommunityOwnership } from "../api/types";
import { EmptyState, ErrorState, Loading } from "../components/states";
import { T } from "../i18n";

// GY-061 contributors: per-community ownership with concentration (a bus-factor
// risk signal, never a vulnerability), advisory escalation (never contacts
// anyone), owner→graph links, and admin corrections. The correction form posts
// to the server, which enforces admin authority and audits the change; a
// non-admin gets a clear permission message.

interface Props {
  client: GraphyClient;
  projectId: string;
  onExploreCommunity: (communityId: number) => void;
}

export function Contributors({ client, projectId, onExploreCommunity }: Props) {
  const [data, setData] = useState<CommunityOwnership[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(false);
    client
      .contributors({ projectId })
      .then((d) => live && setData(d))
      .catch((e) => {
        if (e instanceof ApiError || e instanceof Error) setError(true);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [client, projectId, reloadToken]);

  if (error) return <ErrorState onRetry={() => setReloadToken((t) => t + 1)} />;
  if (loading || !data) return <Loading />;
  if (data.length === 0) return <EmptyState message={T.contributors.none} />;

  return (
    <div className="overview-grid">
      {data.map((co) => (
        <CommunityCard
          key={co.communityId}
          co={co}
          onExplore={() => onExploreCommunity(co.communityId)}
          onCorrect={async (owners, reason) => {
            await client.correctOwnership({ projectId, communityId: co.communityId, owners, reason });
            setReloadToken((t) => t + 1);
          }}
        />
      ))}
    </div>
  );
}

function CommunityCard({
  co,
  onExplore,
  onCorrect,
}: {
  co: CommunityOwnership;
  onExplore: () => void;
  onCorrect: (owners: string[], reason: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [owners, setOwners] = useState(co.owners.map((o) => o.owner).join(", "));
  const [reason, setReason] = useState("");
  const [denied, setDenied] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setDenied(false);
    try {
      await onCorrect(
        owners.split(",").map((s) => s.trim()).filter(Boolean),
        reason,
      );
      setEditing(false);
    } catch {
      setDenied(true);
    }
  };

  return (
    <section className="card">
      <div className="stat-row">
        <h3>
          {T.contributors.community} #{co.communityId}
        </h3>
        {co.concentrated && (
          <span className="tag tag-high" title={T.contributors.concentratedHint}>
            {T.contributors.concentrated}
          </span>
        )}
        {co.corrected && <span className="tag">{T.contributors.corrected}</span>}
      </div>
      <p className="muted">{T.contributors.members(co.memberCount)}</p>

      <h4 className="muted">{T.contributors.owners}</h4>
      <ul className="node-list">
        {co.owners.map((o) => (
          <li key={o.owner}>
            <span>{o.owner}</span>
            <span>
              <span className="tag">{Math.round(o.share * 100)}%</span>
              <span className="tag">{o.method}</span>
              <span className="tag">{o.confidence}</span>
            </span>
          </li>
        ))}
        {co.owners.length === 0 && <li className="muted">{T.contributors.unknown(100)}</li>}
      </ul>
      {co.unknownShare > 0 && co.owners.length > 0 && (
        <p className="muted">{T.contributors.unknown(Math.round(co.unknownShare * 100))}</p>
      )}

      {co.escalation.length > 0 && (
        <>
          <h4 className="muted">{T.contributors.escalation}</h4>
          <p>{co.escalation.join(" → ")}</p>
          <p className="muted">{co.note}</p>
        </>
      )}

      <div className="inspector-actions">
        <button className="btn" onClick={onExplore}>
          {T.contributors.viewInExplorer}
        </button>
        <button className="btn btn-ghost" onClick={() => setEditing((v) => !v)} aria-expanded={editing}>
          {T.contributors.correct}
        </button>
      </div>

      {editing && (
        <form className="mcp-form" onSubmit={submit}>
          <label className="search">
            {T.contributors.ownersInput}
            <input aria-label={T.contributors.ownersInput} value={owners} onChange={(e) => setOwners(e.target.value)} />
          </label>
          <label className="search">
            {T.contributors.reason}
            <input aria-label={T.contributors.reason} value={reason} onChange={(e) => setReason(e.target.value)} />
          </label>
          <button type="submit" className="btn selected-tab">
            {T.contributors.apply}
          </button>
          {denied && <div className="banner banner-error">{T.contributors.correctionFailed}</div>}
        </form>
      )}
    </section>
  );
}
